/**
 * Multi-City eSIM Finder
 * Intelligent product recommendation engine for multi-country eSIM selection
 */

(function() {
  'use strict';

  // ====================================
  // Configuration & State
  // ====================================

  let config = null;
  let state = {
    selectedCountries: [],
    startDate: null,
    endDate: null,
    duration: 0,
    travelers: 1,
    recommendations: null
  };

  // ====================================
  // Initialization
  // ====================================

  function init() {
    // Load configuration
    const configElement = document.getElementById('esim-product-config');
    if (!configElement) {
      console.error('eSIM product configuration not found');
      return;
    }

    try {
      config = JSON.parse(configElement.textContent);
    } catch (e) {
      console.error('Failed to parse eSIM configuration:', e);
      return;
    }

    // Initialize UI
    setupEventListeners();
    renderPopularDestinations();
    renderCountryList();
    loadSavedState();
  }

  // ====================================
  // Event Listeners
  // ====================================

  function setupEventListeners() {
    // Modal triggers
    document.querySelectorAll('[data-mcef-open]').forEach(btn => {
      btn.addEventListener('click', openModal);
    });

    document.querySelectorAll('[data-mcef-close]').forEach(btn => {
      btn.addEventListener('click', closeModal);
    });

    // Country search
    const searchInput = document.getElementById('mcef-country-search');
    if (searchInput) {
      searchInput.addEventListener('input', handleSearch);
    }

    // Date inputs
    const startDateInput = document.querySelector('[data-mcef-start-date]');
    const endDateInput = document.querySelector('[data-mcef-end-date]');

    if (startDateInput) {
      startDateInput.addEventListener('change', handleDateChange);
      // Set min date to today
      startDateInput.min = new Date().toISOString().split('T')[0];
    }

    if (endDateInput) {
      endDateInput.addEventListener('change', handleDateChange);
    }

    // Quantity controls
    document.querySelectorAll('[data-mcef-qty-minus]').forEach(btn => {
      btn.addEventListener('click', () => updateTravelers(-1));
    });

    document.querySelectorAll('[data-mcef-qty-plus]').forEach(btn => {
      btn.addEventListener('click', () => updateTravelers(1));
    });

    const travelersInput = document.querySelector('[data-mcef-travelers]');
    if (travelersInput) {
      travelersInput.addEventListener('change', (e) => {
        state.travelers = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
        updateUI();
      });
    }

    // Submit button
    document.querySelectorAll('[data-mcef-submit]').forEach(btn => {
      btn.addEventListener('click', showResults);
    });

    // Back button
    document.querySelectorAll('[data-mcef-back]').forEach(btn => {
      btn.addEventListener('click', backToSelection);
    });

    // ESC key to close modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    });
  }

  // ====================================
  // Modal Functions
  // ====================================

  function openModal() {
    const modal = document.querySelector('.mcef-modal');
    if (modal) {
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus search input
      setTimeout(() => {
        const searchInput = document.getElementById('mcef-country-search');
        if (searchInput) searchInput.focus();
      }, 100);
    }
  }

  function closeModal() {
    const modal = document.querySelector('.mcef-modal');
    if (modal) {
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      saveState();
    }
  }

  // ====================================
  // Country Selection
  // ====================================

  function renderPopularDestinations() {
    const container = document.querySelector('[data-mcef-popular]');
    if (!container || !config) return;

    const html = config.popularDestinations.map(code => {
      const country = config.countries[code];
      if (!country) return '';

      return `
        <div class="mcef-country-chip" data-country="${code}" role="button" tabindex="0">
          <img src="${country.flag}" alt="${country.name}" class="mcef-country-flag" />
          <span>${country.name}</span>
        </div>
      `;
    }).join('');

    container.innerHTML = html;

    // Add event listeners
    container.querySelectorAll('.mcef-country-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        toggleCountry(chip.dataset.country);
      });
      chip.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') toggleCountry(chip.dataset.country);
      });
    });
  }

  function renderCountryList() {
    const container = document.querySelector('[data-mcef-country-list]');
    if (!container || !config) return;

    const sortedCountries = Object.entries(config.countries)
      .sort((a, b) => a[1].name.localeCompare(b[1].name));

    const html = sortedCountries.map(([code, country]) => `
      <div class="mcef-country-item" data-country="${code}" role="button" tabindex="0">
        <div class="mcef-country-checkbox"></div>
        <img src="${country.flag}" alt="${country.name}" class="mcef-country-flag" />
        <span class="mcef-country-name">${country.name}</span>
      </div>
    `).join('');

    container.innerHTML = html;

    // Add event listeners
    container.querySelectorAll('.mcef-country-item').forEach(item => {
      item.addEventListener('click', () => {
        toggleCountry(item.dataset.country);
      });
      item.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') toggleCountry(item.dataset.country);
      });
    });
  }

  function toggleCountry(code) {
    const index = state.selectedCountries.indexOf(code);

    if (index > -1) {
      state.selectedCountries.splice(index, 1);
    } else {
      state.selectedCountries.push(code);
    }

    updateUI();
  }

  function removeCountry(code) {
    const index = state.selectedCountries.indexOf(code);
    if (index > -1) {
      state.selectedCountries.splice(index, 1);
      updateUI();
    }
  }

  function handleSearch(e) {
    const query = e.target.value.toLowerCase().trim();
    const items = document.querySelectorAll('.mcef-country-item');

    items.forEach(item => {
      const code = item.dataset.country;
      const country = config.countries[code];
      const nameMatch = country.name.toLowerCase().includes(query);
      const codeMatch = code.toLowerCase().includes(query);

      if (query === '' || nameMatch || codeMatch) {
        item.classList.remove('hidden');
      } else {
        item.classList.add('hidden');
      }
    });
  }

  // ====================================
  // Date Handling
  // ====================================

  function handleDateChange() {
    const startInput = document.querySelector('[data-mcef-start-date]');
    const endInput = document.querySelector('[data-mcef-end-date]');

    if (!startInput || !endInput) return;

    state.startDate = startInput.value;
    state.endDate = endInput.value;

    // Update end date minimum
    if (state.startDate) {
      endInput.min = state.startDate;
    }

    // Calculate duration
    if (state.startDate && state.endDate) {
      const start = new Date(state.startDate);
      const end = new Date(state.endDate);
      const diffTime = Math.abs(end - start);
      state.duration = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } else {
      state.duration = 0;
    }

    updateUI();
  }

  // ====================================
  // Travelers Quantity
  // ====================================

  function updateTravelers(delta) {
    state.travelers = Math.max(1, Math.min(10, state.travelers + delta));

    const input = document.querySelector('[data-mcef-travelers]');
    if (input) {
      input.value = state.travelers;
    }

    updateUI();
  }

  // ====================================
  // UI Updates
  // ====================================

  function updateUI() {
    // Update selected pills
    renderSelectedPills();

    // Update country list checkboxes
    document.querySelectorAll('.mcef-country-item').forEach(item => {
      if (state.selectedCountries.includes(item.dataset.country)) {
        item.classList.add('selected');
      } else {
        item.classList.remove('selected');
      }
    });

    // Update popular chips
    document.querySelectorAll('.mcef-popular-grid .mcef-country-chip').forEach(chip => {
      if (state.selectedCountries.includes(chip.dataset.country)) {
        chip.classList.add('selected');
      } else {
        chip.classList.remove('selected');
      }
    });

    // Show/hide trip details
    const tripDetails = document.querySelector('[data-mcef-trip-details]');
    if (tripDetails) {
      if (state.selectedCountries.length > 0) {
        tripDetails.style.display = 'block';
      } else {
        tripDetails.style.display = 'none';
      }
    }

    // Update duration display
    const durationEl = document.querySelector('[data-mcef-duration]');
    const durationDays = document.querySelector('[data-mcef-duration-days]');

    if (durationEl && durationDays && state.duration > 0) {
      durationDays.textContent = state.duration;
      durationEl.style.display = 'flex';
    } else if (durationEl) {
      durationEl.style.display = 'none';
    }

    // Enable/disable submit button
    const submitBtn = document.querySelector('[data-mcef-submit]');
    if (submitBtn) {
      const isValid = state.selectedCountries.length > 0 &&
                      state.startDate &&
                      state.endDate &&
                      state.duration > 0;
      submitBtn.disabled = !isValid;
    }

    // Update quantity button states
    const minusBtn = document.querySelector('[data-mcef-qty-minus]');
    const plusBtn = document.querySelector('[data-mcef-qty-plus]');

    if (minusBtn) minusBtn.disabled = state.travelers <= 1;
    if (plusBtn) plusBtn.disabled = state.travelers >= 10;
  }

  function renderSelectedPills() {
    const container = document.querySelector('[data-mcef-selected]');
    const wrapper = document.querySelector('[data-mcef-selected-wrapper]');

    if (!container || !wrapper) return;

    if (state.selectedCountries.length === 0) {
      wrapper.style.display = 'none';
      return;
    }

    wrapper.style.display = 'block';

    const html = state.selectedCountries.map(code => {
      const country = config.countries[code];
      return `
        <div class="mcef-selected-pill">
          <img src="${country.flag}" alt="${country.name}" class="mcef-country-flag" style="margin-right: 6px;" />
          <span>${country.name}</span>
          <button type="button" class="mcef-pill-remove" data-remove="${code}" aria-label="Remove ${country.name}">
            ×
          </button>
        </div>
      `;
    }).join('');

    container.innerHTML = html;

    // Add remove listeners
    container.querySelectorAll('[data-remove]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        removeCountry(btn.dataset.remove);
      });
    });
  }

  // ====================================
  // Product Matching Algorithm
  // ====================================

  function findBestProducts() {
    const selected = state.selectedCountries;

    if (selected.length === 0) {
      return { type: 'empty', products: [] };
    }

    // Single country scenario
    if (selected.length === 1) {
      return {
        type: 'single',
        products: [{
          type: 'single-country',
          country: selected[0],
          handle: config.countries[selected[0]].handle
        }],
        upsell: true
      };
    }

    // Find all regional eSIMs that cover any selected countries
    const regionalMatches = [];

    Object.entries(config.regions).forEach(([key, region]) => {
      if (region.isGlobal) {
        regionalMatches.push({
          key,
          region,
          coveragePercent: 100,
          coveredCountries: selected,
          uncoveredCountries: []
        });
        return;
      }

      const covered = selected.filter(c => region.countries.includes(c));
      const uncovered = selected.filter(c => !region.countries.includes(c));
      const coveragePercent = (covered.length / selected.length) * 100;

      if (covered.length > 0) {
        regionalMatches.push({
          key,
          region,
          coveragePercent,
          coveredCountries: covered,
          uncoveredCountries: uncovered
        });
      }
    });

    // Sort by coverage percentage (highest first)
    regionalMatches.sort((a, b) => b.coveragePercent - a.coveragePercent);

    // Check for perfect regional match (100% coverage, non-global)
    const perfectMatch = regionalMatches.find(m =>
      m.coveragePercent === 100 && !m.region.isGlobal
    );

    if (perfectMatch) {
      return {
        type: 'regional',
        primary: {
          type: 'regional',
          handle: perfectMatch.region.handle,
          name: perfectMatch.region.displayName,
          countries: perfectMatch.coveredCountries
        },
        alternatives: getAlternatives(regionalMatches, perfectMatch)
      };
    }

    // Mixed coverage scenario
    const bestRegional = regionalMatches.find(m => !m.region.isGlobal);

    if (bestRegional && bestRegional.coveragePercent >= 50) {
      // Use regional + individual country eSIMs
      const individualESIMs = bestRegional.uncoveredCountries.map(code => ({
        type: 'single-country',
        country: code,
        handle: config.countries[code].handle
      }));

      const bundle = {
        type: 'bundle',
        items: [
          {
            type: 'regional',
            handle: bestRegional.region.handle,
            name: bestRegional.region.displayName,
            countries: bestRegional.coveredCountries
          },
          ...individualESIMs
        ]
      };

      return {
        type: 'bundle',
        primary: bundle,
        alternatives: getAlternatives(regionalMatches, bestRegional)
      };
    }

    // Many countries or no good regional match - recommend global
    if (selected.length >= 5) {
      const globalESIM = regionalMatches.find(m => m.region.isGlobal);

      return {
        type: 'global',
        primary: {
          type: 'global',
          handle: globalESIM.region.handle,
          name: globalESIM.region.displayName,
          countries: selected
        },
        alternatives: []
      };
    }

    // Fallback: individual country eSIMs
    return {
      type: 'individual',
      products: selected.map(code => ({
        type: 'single-country',
        country: code,
        handle: config.countries[code].handle
      }))
    };
  }

  function getAlternatives(regionalMatches, exclude) {
    return regionalMatches
      .filter(m => m.key !== exclude.key)
      .slice(0, 2)
      .map(m => ({
        type: m.region.isGlobal ? 'global' : 'regional',
        handle: m.region.handle,
        name: m.region.displayName,
        countries: m.coveredCountries,
        coveragePercent: m.coveragePercent
      }));
  }

  // ====================================
  // Results Display
  // ====================================

  function showResults() {
    state.recommendations = findBestProducts();

    // Switch to results view
    document.querySelector('[data-mcef-step="selection"]').style.display = 'none';
    document.querySelector('[data-mcef-step="results"]').style.display = 'block';

    renderTripSummary();
    renderResults();
    saveState();
  }

  function backToSelection() {
    document.querySelector('[data-mcef-step="results"]').style.display = 'none';
    document.querySelector('[data-mcef-step="selection"]').style.display = 'block';
  }

  function renderTripSummary() {
    const container = document.querySelector('[data-mcef-summary]');
    if (!container) return;

    const countriesText = state.selectedCountries
      .map(code => config.countries[code].name)
      .join(', ');

    const html = `
      <h3 class="mcef-summary-title">✨ Your Trip Summary</h3>
      <div class="mcef-summary-details">
        <div class="mcef-summary-item">
          <span>📍</span>
          <strong>${countriesText}</strong>
        </div>
        <div class="mcef-summary-item">
          <span>📅</span>
          <strong>${state.duration} days</strong>
        </div>
        <div class="mcef-summary-item">
          <span>👤</span>
          <strong>${state.travelers} ${state.travelers === 1 ? 'traveler' : 'travelers'}</strong>
        </div>
      </div>
    `;

    container.innerHTML = html;
  }

  function renderResults() {
    const container = document.querySelector('[data-mcef-results]');
    if (!container || !state.recommendations) return;

    const rec = state.recommendations;
    let html = '';

    if (rec.type === 'regional') {
      html += renderProductCard(rec.primary, true);
      rec.alternatives.forEach(alt => {
        html += renderProductCard(alt, false);
      });
    } else if (rec.type === 'bundle') {
      html += renderBundleCard(rec.primary, true);
      rec.alternatives.forEach(alt => {
        html += renderProductCard(alt, false);
      });
    } else if (rec.type === 'global') {
      html += renderProductCard(rec.primary, true);
    } else if (rec.type === 'single') {
      html += renderProductCard(rec.products[0], false);
      if (rec.upsell) {
        html += `
          <div class="mcef-empty">
            <div class="mcef-empty-icon">💡</div>
            <p class="mcef-empty-text">
              Traveling to nearby countries? Add them to unlock cheaper regional eSIM options!
            </p>
          </div>
        `;
      }
    } else if (rec.type === 'individual') {
      rec.products.forEach(product => {
        html += renderProductCard(product, false);
      });
    }

    container.innerHTML = html;

    // Add cart button listeners
    container.querySelectorAll('[data-add-to-cart]').forEach(btn => {
      btn.addEventListener('click', () => {
        const handle = btn.dataset.handle;
        addToCart(handle);
      });
    });
  }

  function renderProductCard(product, isRecommended) {
    const recommendedBadge = isRecommended ? '<div class="mcef-result-badge"><span>⭐</span> RECOMMENDED</div>' : '';
    const recommendedClass = isRecommended ? ' recommended' : '';

    const coverage = product.type === 'global' ? 'Works in 130+ countries' :
                     product.type === 'regional' ? `Covers all your destinations in ${product.name}` :
                     `Coverage in ${config.countries[product.country].name}`;

    const cardId = `product-card-${product.handle}`;

    // Fetch variants and render data plan options
    fetchAndRenderVariants(product.handle, cardId, state.duration, state.travelers);

    return `
      <div class="mcef-result-card${recommendedClass}" id="${cardId}" data-product-handle="${product.handle}">
        ${recommendedBadge}
        <h3 class="mcef-result-title">${getProductTitle(product)}</h3>
        <p class="mcef-result-coverage">✓ ${coverage}</p>

        <div class="mcef-plan-selector">
          <div class="mcef-plan-title">Choose your data plan</div>
          <div class="mcef-plan-options" data-plan-options>
            <div class="mcef-plan-loading">Loading plans...</div>
          </div>
        </div>

        <div class="mcef-result-footer" style="display: none;" data-footer>
          <div>
            <div class="mcef-result-price">
              <span data-selected-price>$0.00</span>
              <span class="mcef-result-price-note">for ${state.travelers} ${state.travelers === 1 ? 'eSIM' : 'eSIMs'}</span>
            </div>
          </div>
          <button
            type="button"
            class="mcef-add-to-cart-btn"
            data-add-to-cart
            data-handle="${product.handle}"
            data-variant-id=""
            disabled
          >
            Add to Cart
          </button>
        </div>
      </div>
    `;
  }

  function renderBundleCard(bundle, isRecommended) {
    const recommendedBadge = isRecommended ? '<div class="mcef-result-badge"><span>⭐</span> BEST VALUE BUNDLE</div>' : '';
    const recommendedClass = isRecommended ? ' recommended' : '';

    const items = bundle.items.map(item => {
      if (item.type === 'regional') {
        return `• ${item.name}`;
      } else {
        return `• ${config.countries[item.country].name} Single Country eSIM`;
      }
    }).join('<br>');

    const cardId = `bundle-card-${bundle.items[0].handle}`;

    // Fetch real price for bundle (sum of all items)
    fetchBundlePrice(bundle.items, state.duration, state.travelers, cardId);

    return `
      <div class="mcef-result-card${recommendedClass}" id="${cardId}">
        ${recommendedBadge}
        <h3 class="mcef-result-title">Perfect Coverage Bundle</h3>
        <p class="mcef-result-coverage">Complete coverage for your trip:</p>
        <div style="margin: 16px 0; font-size: 14px; line-height: 1.6; color: var(--mcef-dark);">
          ${items}
        </div>
        <ul class="mcef-result-features">
          <li>10GB data per eSIM</li>
          <li>30 days validity</li>
          <li>High-speed 4G/5G</li>
          <li>All eSIMs activate independently</li>
        </ul>
        <div class="mcef-result-footer">
          <div>
            <div class="mcef-result-price">
              <span class="price-loading">Loading...</span>
              <span class="mcef-result-price-note">for ${state.travelers} ${state.travelers === 1 ? 'set' : 'sets'}</span>
            </div>
          </div>
          <button
            type="button"
            class="mcef-add-to-cart-btn"
            data-add-to-cart
            data-handle="${bundle.items[0].handle}"
          >
            Add All to Cart
          </button>
        </div>
      </div>
    `;
  }

  function getProductTitle(product) {
    if (product.type === 'global') {
      return 'Global eSIM';
    } else if (product.type === 'regional') {
      return product.name;
    } else {
      return `${config.countries[product.country].name} eSIM`;
    }
  }

  // ====================================
  // Shopify Cart Integration
  // ====================================

  function addToCart(handle) {
    const btn = document.querySelector(`[data-handle="${handle}"][data-add-to-cart]`);
    if (!btn) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) {
      alert('Please select a data plan first');
      return;
    }

    const originalText = btn.textContent;
    btn.classList.add('loading');
    btn.textContent = 'Adding...';
    btn.disabled = true;

    // Add to cart using the selected variant ID
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: state.travelers
      })
    })
      .then(res => res.json())
      .then(data => {
        // Success!
        btn.classList.remove('loading');
        btn.textContent = '✓ Added!';

        // Update cart count
        updateCartCount();

        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        btn.classList.remove('loading');
        btn.textContent = 'Error - Try Again';
        btn.disabled = false;

        setTimeout(() => {
          btn.textContent = originalText;
        }, 2000);
      });
  }

  function findBestVariant(variants, duration) {
    // Filter variants based on duration and prefer 10GB as "best choice"
    let suitable = [];

    // Match validity period to trip duration
    if (duration <= 7) {
      suitable = variants.filter(v => {
        const title = v.title.toLowerCase();
        return title.includes('7 day') || title.includes('7day') ||
               title.includes('7 days') || title.includes('7days');
      });
    } else if (duration <= 14) {
      suitable = variants.filter(v => {
        const title = v.title.toLowerCase();
        return title.includes('14 day') || title.includes('14day') ||
               title.includes('14 days') || title.includes('14days');
      });
    } else {
      suitable = variants.filter(v => {
        const title = v.title.toLowerCase();
        return title.includes('30 day') || title.includes('30day') ||
               title.includes('30 days') || title.includes('30days');
      });
    }

    // If no suitable duration found, get any 30-day variant
    if (suitable.length === 0) {
      suitable = variants.filter(v => {
        const title = v.title.toLowerCase();
        return title.includes('30 day') || title.includes('30days');
      });
    }

    // Prefer 10GB variant (best choice)
    let best = suitable.find(v => v.title.toLowerCase().includes('10gb'));

    // Fallback to first suitable variant
    if (!best && suitable.length > 0) {
      best = suitable[0];
    }

    // Last resort: return first variant
    if (!best && variants.length > 0) {
      best = variants[0];
    }

    return best;
  }

  function updateCartCount() {
    // Update cart count in header if it exists
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        const cartCount = document.querySelector('.cart-count, [data-cart-count]');
        if (cartCount) {
          cartCount.textContent = cart.item_count;
        }
      })
      .catch(err => console.error('Error updating cart count:', err));
  }

  // ====================================
  // Variant Fetching and Rendering
  // ====================================

  function fetchAndRenderVariants(handle, cardId, duration, travelers) {
    fetch(`/products/${handle}.js`)
      .then(res => res.json())
      .then(product => {
        const card = document.getElementById(cardId);
        if (!card) return;

        const optionsContainer = card.querySelector('[data-plan-options]');
        const footer = card.querySelector('[data-footer]');

        // Filter and sort variants (1GB, 5GB, 10GB, 20GB, Unlimited)
        const filteredVariants = filterAndSortVariants(product.variants, duration);

        if (filteredVariants.length === 0) {
          optionsContainer.innerHTML = '<div class="mcef-plan-error">No plans available</div>';
          return;
        }

        // Get ALL unlimited variants (all durations) for the dropdown
        const allUnlimitedVariants = getAllUnlimitedVariants(product.variants);

        // Render variant tiles
        const variantTilesHTML = filteredVariants.map((variant, index) => {
          const is10GB = variant.dataSize === '10GB';
          const isUnlimited = variant.isUnlimited;
          const isDefault = is10GB || (index === 0 && !filteredVariants.some(v => v.dataSize === '10GB'));

          return renderVariantTile(variant, isDefault, is10GB, isUnlimited, handle, travelers, allUnlimitedVariants);
        }).join('');

        optionsContainer.innerHTML = variantTilesHTML;

        // Show footer now that plans are loaded
        if (footer) {
          footer.style.display = 'flex';
        }

        // Add event listeners to plan options
        optionsContainer.querySelectorAll('[data-plan-radio]').forEach(radio => {
          radio.addEventListener('change', (e) => {
            if (e.target.checked) {
              handlePlanSelection(card, e.target.value, travelers, handle);
            }
          });
        });

        // Add event listeners to unlimited dropdowns
        optionsContainer.querySelectorAll('[data-unlimited-select]').forEach(select => {
          select.addEventListener('change', (e) => {
            const newVariantId = e.target.value;
            const option = e.target.selectedOptions[0];
            const newPrice = option.dataset.price;

            // Update the radio button value and price
            const radioInput = e.target.closest('[data-plan-label]').querySelector('[data-plan-radio]');
            radioInput.value = newVariantId;
            radioInput.dataset.price = newPrice;

            // Update the displayed price
            const priceSpan = e.target.closest('[data-plan-label]').querySelector('.mcef-plan-price');
            if (priceSpan) {
              priceSpan.textContent = `£${newPrice}`;
            }

            // If this plan is selected, update the footer price too
            if (radioInput.checked) {
              handlePlanSelection(card, newVariantId, travelers, handle);
            }
          });
        });

        // Auto-select default (10GB or first)
        const defaultRadio = optionsContainer.querySelector('[data-plan-radio]:checked');
        if (defaultRadio) {
          handlePlanSelection(card, defaultRadio.value, travelers, handle);
        }
      })
      .catch(err => {
        console.error(`Error fetching variants for ${handle}:`, err);
        const card = document.getElementById(cardId);
        if (card) {
          const optionsContainer = card.querySelector('[data-plan-options]');
          if (optionsContainer) {
            optionsContainer.innerHTML = '<div class="mcef-plan-error">Unable to load plans</div>';
          }
        }
      });
  }

  function getAllUnlimitedVariants(variants) {
    // Get all unlimited variants across all durations
    const unlimited = variants.filter(v => v.title.toLowerCase().includes('unlimited'));

    // Parse and deduplicate by days
    const variantsByDays = new Map();

    unlimited.forEach(v => {
      const title = v.title.toLowerCase();
      let days = 30; // default
      let daysLabel = '30 Days';

      // Extract days from title with better pattern matching
      const dayMatch = title.match(/(\d+)\s*days?/);
      if (dayMatch) {
        days = parseInt(dayMatch[1]);
        daysLabel = `${days} Days`;
      }

      // Only keep one variant per day duration (use the first one found)
      if (!variantsByDays.has(days)) {
        variantsByDays.set(days, {
          ...v,
          days,
          daysLabel,
          sortOrder: days
        });
      }
    });

    // Convert map to array and sort
    return Array.from(variantsByDays.values()).sort((a, b) => a.sortOrder - b.sortOrder);
  }

  function filterAndSortVariants(variants, duration) {
    // Filter variants that match the trip duration
    let suitable = variants.filter(v => {
      const title = v.title.toLowerCase();

      // Match duration
      if (duration <= 7) {
        return title.includes('7 day') || title.includes('7day') ||
               title.includes('7 days') || title.includes('7days');
      } else if (duration <= 14) {
        return title.includes('14 day') || title.includes('14day') ||
               title.includes('14 days') || title.includes('14days');
      } else {
        return title.includes('30 day') || title.includes('30day') ||
               title.includes('30 days') || title.includes('30days');
      }
    });

    // If no matches, default to 30-day variants
    if (suitable.length === 0) {
      suitable = variants.filter(v => {
        const title = v.title.toLowerCase();
        return title.includes('30 day') || title.includes('30days');
      });
    }

    // Parse and filter to only 1GB, 5GB, 10GB, 20GB, Unlimited
    const parsed = suitable.map(v => {
      const title = v.title.toLowerCase();
      const isUnlimited = title.includes('unlimited');

      let dataSize = '';
      if (isUnlimited) {
        dataSize = 'Unlimited';
      } else if (title.includes('1gb') || title.includes('1 gb')) {
        dataSize = '1GB';
      } else if (title.includes('5gb') || title.includes('5 gb')) {
        dataSize = '5GB';
      } else if (title.includes('10gb') || title.includes('10 gb')) {
        dataSize = '10GB';
      } else if (title.includes('20gb') || title.includes('20 gb')) {
        dataSize = '20GB';
      }

      return {
        ...v,
        dataSize,
        isUnlimited,
        sortOrder: isUnlimited ? 999 : parseInt(dataSize) || 0
      };
    }).filter(v => v.dataSize); // Only keep recognized sizes

    // Sort by data size (1GB, 5GB, 10GB, 20GB, then Unlimited)
    parsed.sort((a, b) => a.sortOrder - b.sortOrder);

    return parsed;
  }

  function renderVariantTile(variant, isDefault, isBestChoice, isUnlimited, handle, travelers, allUnlimitedVariants = []) {
    const uniqueId = `plan-${handle}-${variant.id}`;
    const pricePerItem = (variant.price / 100).toFixed(2);
    const totalPrice = (pricePerItem * travelers).toFixed(2);

    const bestChoiceBadge = isBestChoice ? '<span class="mcef-plan-best-choice">Best Choice</span>' : '';
    const powerUserBadge = isUnlimited ? '<span class="mcef-plan-power-user">Best for Power Users</span>' : '';

    // For Unlimited plans, render a dropdown instead of just the data size
    let dataContent = `<span class="mcef-plan-data">${variant.dataSize}</span>`;

    if (isUnlimited && allUnlimitedVariants.length > 0) {
      const dropdownOptions = allUnlimitedVariants.map(v => {
        const price = (v.price / 100 * travelers).toFixed(2);
        return `<option value="${v.id}" data-price="${price}" ${v.id === variant.id ? 'selected' : ''}>${v.daysLabel}</option>`;
      }).join('');

      dataContent = `
        <div class="mcef-plan-unlimited-wrap">
          <span class="mcef-plan-data">Unlimited</span>
          <select class="mcef-plan-unlimited-select" data-unlimited-select>
            ${dropdownOptions}
          </select>
        </div>
      `;
    }

    return `
      <label class="mcef-plan-option ${isDefault ? 'is-selected' : ''} ${isBestChoice ? 'is-best-choice' : ''} ${isUnlimited ? 'is-unlimited' : ''}" data-plan-label>
        <input
          type="radio"
          name="plan-${handle}"
          value="${variant.id}"
          id="${uniqueId}"
          data-plan-radio
          data-price="${totalPrice}"
          ${isDefault ? 'checked' : ''}
          class="mcef-plan-radio"
        >
        ${powerUserBadge}
        ${bestChoiceBadge}
        ${dataContent}
        <span class="mcef-plan-price">£${totalPrice}</span>
        <span class="mcef-plan-dot"></span>
        ${isUnlimited ? '<span class="mcef-plan-note">Unlimited data · Speed reduced after fair use</span>' : ''}
      </label>
    `;
  }

  function handlePlanSelection(card, variantId, travelers, handle) {
    // Update selected state on labels
    card.querySelectorAll('[data-plan-label]').forEach(label => {
      label.classList.remove('is-selected');
    });
    const selectedRadio = card.querySelector(`[data-plan-radio][value="${variantId}"]`);
    if (selectedRadio) {
      selectedRadio.closest('[data-plan-label]').classList.add('is-selected');

      // Update price
      const price = selectedRadio.dataset.price;
      const priceElement = card.querySelector('[data-selected-price]');
      if (priceElement) {
        priceElement.textContent = `£${price}`;
      }

      // Enable add to cart button and store variant ID
      const addButton = card.querySelector('[data-add-to-cart]');
      if (addButton) {
        addButton.disabled = false;
        addButton.dataset.variantId = variantId;
      }
    }
  }

  // ====================================
  // Price Fetching from Shopify
  // ====================================

  function fetchProductPrice(handle, duration, travelers, cardId) {
    fetch(`/products/${handle}.js`)
      .then(res => res.json())
      .then(product => {
        const variant = findBestVariant(product.variants, duration);
        if (variant) {
          const pricePerItem = (variant.price / 100).toFixed(2);
          const totalPrice = (pricePerItem * travelers).toFixed(2);

          // Update the card with real price
          const card = document.getElementById(cardId);
          if (card) {
            const priceElement = card.querySelector('.mcef-result-price');
            if (priceElement) {
              const loadingSpan = priceElement.querySelector('.price-loading');
              if (loadingSpan) {
                loadingSpan.textContent = `£${totalPrice}`;
                loadingSpan.classList.remove('price-loading');
              }
            }
          }
        }
      })
      .catch(err => {
        console.error(`Error fetching price for ${handle}:`, err);
        const card = document.getElementById(cardId);
        if (card) {
          const loadingSpan = card.querySelector('.price-loading');
          if (loadingSpan) {
            loadingSpan.textContent = 'Price unavailable';
          }
        }
      });
  }

  function fetchBundlePrice(items, duration, travelers, cardId) {
    // Fetch all product prices and sum them
    const pricePromises = items.map(item =>
      fetch(`/products/${item.handle}.js`)
        .then(res => res.json())
        .then(product => {
          const variant = findBestVariant(product.variants, duration);
          return variant ? variant.price / 100 : 0;
        })
        .catch(() => 0)
    );

    Promise.all(pricePromises)
      .then(prices => {
        const totalPerSet = prices.reduce((sum, price) => sum + price, 0);
        const totalPrice = (totalPerSet * travelers).toFixed(2);

        // Update the card with real price
        const card = document.getElementById(cardId);
        if (card) {
          const priceElement = card.querySelector('.mcef-result-price');
          if (priceElement) {
            const loadingSpan = priceElement.querySelector('.price-loading');
            if (loadingSpan) {
              loadingSpan.textContent = `£${totalPrice}`;
              loadingSpan.classList.remove('price-loading');
            }
          }
        }
      })
      .catch(err => {
        console.error('Error fetching bundle price:', err);
        const card = document.getElementById(cardId);
        if (card) {
          const loadingSpan = card.querySelector('.price-loading');
          if (loadingSpan) {
            loadingSpan.textContent = 'Price unavailable';
          }
        }
      });
  }


  // ====================================
  // State Persistence
  // ====================================

  function saveState() {
    try {
      localStorage.setItem('mcef-state', JSON.stringify({
        selectedCountries: state.selectedCountries,
        startDate: state.startDate,
        endDate: state.endDate,
        travelers: state.travelers
      }));
    } catch (e) {
      console.error('Failed to save state:', e);
    }
  }

  function loadSavedState() {
    try {
      const saved = localStorage.getItem('mcef-state');
      if (saved) {
        const data = JSON.parse(saved);

        // Only restore if recent (within 24 hours)
        const savedTime = localStorage.getItem('mcef-state-time');
        if (savedTime && Date.now() - parseInt(savedTime) < 24 * 60 * 60 * 1000) {
          state.selectedCountries = data.selectedCountries || [];
          state.startDate = data.startDate || null;
          state.endDate = data.endDate || null;
          state.travelers = data.travelers || 1;

          // Restore UI
          if (state.startDate) {
            const startInput = document.querySelector('[data-mcef-start-date]');
            if (startInput) startInput.value = state.startDate;
          }

          if (state.endDate) {
            const endInput = document.querySelector('[data-mcef-end-date]');
            if (endInput) endInput.value = state.endDate;
          }

          const travelersInput = document.querySelector('[data-mcef-travelers]');
          if (travelersInput) travelersInput.value = state.travelers;

          handleDateChange();
          updateUI();
        }
      }

      localStorage.setItem('mcef-state-time', Date.now().toString());
    } catch (e) {
      console.error('Failed to load state:', e);
    }
  }

  // ====================================
  // Initialize on DOM Ready
  // ====================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ====================================
  // Page-Based Multi-Country Finder
  // ====================================

  /**
   * Initialize the page-based multi-country eSIM finder
   * (for dedicated page template, not modal)
   */
  window.initMultiCountryESIMPage = function() {
    const countrySelect = document.querySelector('[data-mcef-page-country-select]');
    const travelersInput = document.querySelector('[data-mcef-page-travelers-input]');
    const decreaseBtn = document.querySelector('[data-mcef-page-travelers-decrease]');
    const increaseBtn = document.querySelector('[data-mcef-page-travelers-increase]');
    const searchBtn = document.querySelector('[data-mcef-page-search-btn]');
    const resultsContainer = document.querySelector('[data-mcef-page-results]');
    const emptyState = document.querySelector('[data-mcef-page-empty-state]');

    if (!countrySelect || !searchBtn || !resultsContainer) {
      return; // Not on the page template
    }

    // Load configuration
    const configElement = document.getElementById('esim-product-config');
    if (!configElement) {
      console.error('eSIM product configuration not found');
      return;
    }

    let pageConfig;
    try {
      pageConfig = JSON.parse(configElement.textContent);
    } catch (e) {
      console.error('Failed to parse eSIM configuration:', e);
      return;
    }

    // Populate country select with all countries from config
    function populateCountrySelect() {
      const countries = pageConfig.countries || [];
      const sortedCountries = countries.sort((a, b) => a.name.localeCompare(b.name));

      countrySelect.innerHTML = sortedCountries.map(country =>
        `<option value="${country.iso}">${country.flag} ${country.name}</option>`
      ).join('');
    }

    // Traveler count controls
    function updateTravelers(delta) {
      const current = parseInt(travelersInput.value) || 1;
      const newValue = Math.max(1, Math.min(10, current + delta));
      travelersInput.value = newValue;
    }

    decreaseBtn?.addEventListener('click', () => updateTravelers(-1));
    increaseBtn?.addEventListener('click', () => updateTravelers(1));

    travelersInput?.addEventListener('input', (e) => {
      let value = parseInt(e.target.value) || 1;
      value = Math.max(1, Math.min(10, value));
      e.target.value = value;
    });

    // Search button handler
    searchBtn.addEventListener('click', async () => {
      const selectedOptions = Array.from(countrySelect.selectedOptions);
      const selectedCountries = selectedOptions.map(opt => opt.value);
      const travelers = parseInt(travelersInput.value) || 1;

      if (selectedCountries.length === 0) {
        alert('Please select at least one country');
        return;
      }

      // Show loading state
      searchBtn.disabled = true;
      searchBtn.textContent = 'Finding Plans...';
      resultsContainer.style.display = 'none';
      emptyState.style.display = 'none';

      try {
        // Get matching products
        const matches = await findMatchingProducts(selectedCountries, pageConfig);

        // Render results
        if (matches.length === 0) {
          showNoResults();
        } else {
          await renderPageResults(matches, travelers);
        }
      } catch (error) {
        console.error('Search error:', error);
        alert('An error occurred while searching. Please try again.');
      } finally {
        searchBtn.disabled = false;
        searchBtn.textContent = 'Find My eSIM Plans';
      }
    });

    // Find matching products based on selected countries
    async function findMatchingProducts(selectedCountries, config) {
      const products = config.products || [];
      const matches = [];

      for (const product of products) {
        const productCountries = product.countries || [];

        // Check if product covers ALL selected countries
        const coversAll = selectedCountries.every(iso =>
          productCountries.includes(iso)
        );

        if (coversAll) {
          matches.push({
            ...product,
            matchedCountries: selectedCountries.length,
            coveragePercent: 100
          });
        }
      }

      // Sort by best match
      matches.sort((a, b) => {
        // Prefer exact matches
        if (a.countries.length === b.countries.length) {
          return a.title.localeCompare(b.title);
        }
        return a.countries.length - b.countries.length;
      });

      return matches;
    }

    // Render results on the page
    async function renderPageResults(matches, travelers) {
      resultsContainer.innerHTML = '';
      resultsContainer.style.display = 'grid';
      emptyState.style.display = 'none';

      for (const match of matches) {
        const cardHTML = await createProductCard(match, travelers);
        const cardWrapper = document.createElement('div');
        cardWrapper.innerHTML = cardHTML;
        resultsContainer.appendChild(cardWrapper.firstElementChild);
      }

      // Setup card interactions
      setupCardInteractions();
    }

    // Create product card HTML (reuse from modal version)
    async function createProductCard(product, travelers) {
      const { handle, title, countries } = product;
      const countryNames = countries
        .map(iso => {
          const c = pageConfig.countries.find(country => country.iso === iso);
          return c ? c.name : iso;
        })
        .slice(0, 3)
        .join(', ');

      const moreCount = countries.length > 3 ? `+${countries.length - 3} more` : '';

      return `
        <div class="mcef-card" data-product-handle="${handle}">
          <div class="mcef-card-header">
            <div class="mcef-card-flags">
              ${countries.slice(0, 5).map(iso => {
                const c = pageConfig.countries.find(country => country.iso === iso);
                return c ? `<span class="mcef-card-flag">${c.flag}</span>` : '';
              }).join('')}
              ${countries.length > 5 ? `<span class="mcef-card-flag-more">+${countries.length - 5}</span>` : ''}
            </div>
            <h3 class="mcef-card-title">${title}</h3>
            <p class="mcef-card-countries">${countryNames}${moreCount ? ' ' + moreCount : ''}</p>
          </div>

          <div class="mcef-card-body">
            <div class="mcef-plan-options" data-plan-options>
              <div class="mcef-plan-loading">
                <div class="mcef-plan-loading-spinner"></div>
                <span>Loading plans...</span>
              </div>
            </div>
          </div>

          <div class="mcef-card-footer">
            <div class="mcef-card-price">
              <span class="mcef-card-price-label">Total for ${travelers} traveler${travelers > 1 ? 's' : ''}</span>
              <span class="mcef-card-price-amount" data-selected-price>
                <span class="mcef-card-price-loading">Loading...</span>
              </span>
            </div>
            <button class="mcef-card-btn" data-add-to-cart disabled>
              <span data-btn-text>Select Plan</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" data-btn-icon>
                <path d="M15 10l-5 5-5-5M10 5v10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      `;
    }

    // Setup card interactions (variant loading, selection, add to cart)
    function setupCardInteractions() {
      const cards = document.querySelectorAll('.mcef-card');

      cards.forEach(card => {
        const handle = card.dataset.productHandle;
        const travelers = parseInt(travelersInput.value) || 1;

        // Load variants for this product
        fetchAndRenderVariants(handle, travelers, card);
      });
    }

    // Show no results state
    function showNoResults() {
      resultsContainer.style.display = 'none';
      emptyState.style.display = 'block';
      emptyState.innerHTML = `
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" class="mcef-page-empty-icon">
          <circle cx="32" cy="32" r="30" stroke="currentColor" stroke-width="2" opacity="0.2"/>
          <path d="M22 22l20 20M42 22L22 42" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <h3 class="mcef-page-empty-title">No matching eSIM plans found</h3>
        <p class="mcef-page-empty-text">Try selecting different countries or fewer destinations</p>
      `;
    }

    // Initialize
    populateCountrySelect();
  };

})();
